import {
  Autocomplete,
  AutocompleteProps,
  Button,
  createFilterOptions,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  TextFieldProps,
  useTheme,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { Alert } from "@mui/material";
import { FORM_ERROR } from "final-form";
import useDebounce from "hooks/useDebounce";
import { pick } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { Form } from "react-final-form";
import { useIntl } from "react-intl";
import { IdName } from "types/common-types";
import translateError from "utils/translateError";
import { makeStyles } from "helpers/StyleHelper";

export type SelectCreatableProps<TOption extends IdName, TNewItem> = {
  onChange: (value: TOption | null) => void;
  initialValue?: TOption | null;
  fetchOptions: (
    search: string
  ) => Promise<{
    result: TOption[];
    relatedSearchValue: string;
  }>;
  onEntityFormSubmit: (newItem: TNewItem) => Promise<TOption>;
  entityFormFields: JSX.Element;
  textFieldProps?: Partial<TextFieldProps>;
  entityName: string;
  idKey: keyof TOption & keyof TNewItem;
  labelField: keyof TOption & keyof TNewItem;
  editFormInit?: TNewItem | null;
} & Partial<
  Omit<
    AutocompleteProps<TOption, false, boolean, boolean>,
    "onInputChange" | "onChange" | "multiple"
  >
>;

const useStyles = makeStyles({
  inputPadding: {
    padding: "5px",
  },
  loaderMargin: {
    marginRight: "10px",
  },
});

const SelectCreatable = <TOption extends IdName, TNewItem>({
  onChange,
  fetchOptions,
  onEntityFormSubmit,
  entityFormFields,
  textFieldProps,
  entityName,
  idKey,
  initialValue,
  labelField,
  editFormInit,
  value: propValue,
  ...rest
}: SelectCreatableProps<TOption, TNewItem>) => {
  const { formatMessage: t } = useIntl();
  const filter = createFilterOptions<TOption>();
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearchValue = useDebounce(
    searchValue.trim().toLowerCase(),
    500
  );
  const styles = useStyles(useTheme());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [lastSavedItem, setLastSavedItem] = useState<TNewItem | null>(null);
  const [editable, setEditable] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [options, setOption] = useState<TOption[]>([]);
  const [value, setValue] = useState<TOption | null>(initialValue ?? null);
  const [loading, setIsLoading] = useState(false);

  const init = useMemo<TNewItem>(
    () => ({
      ...(pick(lastSavedItem ?? editFormInit ?? {}, [
        "id",
        "applicationId",
        "name",
        "description",
        "value",
      ]) as TNewItem),
    }),
    [editFormInit, lastSavedItem]
  );

  useEffect(() => {
    const isNothingSelected = !value;
    const isCreation = !editFormInit;

    const isOtherEntitySelected =
      value &&
      ((value[idKey] as unknown) as string) !==
        ((init[idKey] as unknown) as string);

    const isSearchValueDifferentFromEditableItemName =
      !!searchValue &&
      ((init[labelField] as unknown) as string) !== searchValue;

    setEditable(
      !isNothingSelected &&
        !isCreation &&
        !isOtherEntitySelected &&
        !isSearchValueDifferentFromEditableItemName
    );
  }, [editFormInit, idKey, labelField, init, searchValue, value]);

  useEffect(() => {
    async function getData() {
      setIsLoading(true);

      var response = await fetchOptions(debouncedSearchValue);
      setOption(response.result ?? []);

      if (!debouncedSearchValue) {
        const matchingValue =
          typeof propValue === "string"
            ? response.result.find((x) => x.id === propValue)
            : propValue;

        setValue(matchingValue ?? null);
      }

      if (response.relatedSearchValue === debouncedSearchValue) {
        setIsLoading(false);
      }
    }

    getData();
  }, [debouncedSearchValue, fetchOptions, propValue]);

  const onSubmit = async (newItem: TNewItem) => {
    try {
      const upsertedItem = await onEntityFormSubmit(newItem);
      setLastSavedItem({ ...newItem, id: upsertedItem[idKey] });
      setValue(upsertedItem);
      onChange(upsertedItem);
      setDialogOpen(false);
      setEditMode(false);
    } catch (error) {
      return { [FORM_ERROR]: translateError(error, t) };
    }
  };

  return <>
    <Autocomplete
      data-cy={`select${entityName.replace(/[^\w]/gi, "")}`}
      id={`creatable-autocomplete-for-${entityName}`}
      loading={loading}
      options={options}
      value={value}
      autoComplete
      multiple={false}
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      isOptionEqualToValue={(option, value) => option[idKey] === value[idKey]}
      getOptionLabel={(option) => {
        if (typeof option === "string") {
          return option;
        }

        if ((option as any).newItemName) {
          return (option as any).newItemName;
        }

        return (option[labelField] as unknown) as string;
      }}
      onChange={(_, newValue) => {
        if (typeof newValue === "string") {
          setDialogOpen(true);
        } else if (newValue && (newValue as any).newItemName) {
          setDialogOpen(true);
        } else {
          setValue(newValue);
          onChange(newValue);
        }
      }}
      filterOptions={(options, params) => {
        const filtered = filter(options, params);

        if (!loading && params.inputValue !== "" && filtered.length === 0) {
          const newOption: any = {
            newItemName: `Add new ${entityName}`,
          };

          filtered.push(newOption);
        }

        return filtered;
      }}
      onInputChange={(_, search) => {
        setSearchValue(search);
      }}
      renderInput={(params) => (
        <TextField
          variant="standard"
          label={entityName}
          {...params}
          {...textFieldProps}
          InputProps={{
            ...textFieldProps?.InputProps,
            ...params.InputProps,
            sx: styles.inputPadding,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress
                    size={16}
                    sx={styles.loaderMargin}
                  />
                ) : null}
                {editable && (
                  <Button
                    disabled={!editable}
                    variant="contained"
                    onClick={() => {
                      setEditMode(true);
                      setDialogOpen(true);
                    }}
                  >
                    {t({ id: "editLabel" })}
                  </Button>
                )}
              </>
            ),
          }}
        />
      )}
      {...rest}
    />
    <Dialog
      open={dialogOpen}
      onClose={() => {
        setDialogOpen(false);
        setEditMode(false);
      }}
      maxWidth="lg"
      fullWidth
      disableEnforceFocus
      disableEscapeKeyDown
    >
      <Form
        initialValues={editMode ? init : null}
        onSubmit={onSubmit}
        render={({ handleSubmit, submitting, submitError }) => (
          <form onSubmit={handleSubmit}>
            <DialogTitle>
              {editMode ? t({ id: "update" }) : t({ id: "createNew" })}&nbsp;
              {entityName}
            </DialogTitle>
            <DialogContent>
              {entityFormFields}
              {submitError && <Alert severity="error">{submitError}</Alert>}
            </DialogContent>
            <DialogActions>
              <Button
                disabled={submitting}
                onClick={() => {
                  setDialogOpen(false);
                  setEditMode(false);
                }}
              >
                {t({ id: "cancelButton" })}
              </Button>
              <Button disabled={submitting} type="submit">
                {t({ id: "saveButton" })}
                {submitting && (
                  <CircularProgress
                    style={{ marginLeft: "10px" }}
                    color="inherit"
                    size={14}
                  />
                )}
              </Button>
            </DialogActions>
          </form>
        )}
      />
    </Dialog>
  </>;
};

export default SelectCreatable;
